import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { eur } from "@/components/LineItemsEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Route, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Deplacements() {
  const [rows, setRows] = useState([]); const [summary, setSummary] = useState(null); const [loading, setLoading] = useState(true); const [open, setOpen] = useState(false);
  const empty = { date:"", label:"", distance_km:"", round_trip:false };
  const [f, setF] = useState(empty); const [rate, setRate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: t }, { data: s }] = await Promise.all([api.get("/mileage/trips"), api.get("/mileage/summary")]);
      setRows(t); setSummary(s); setRate(String(s.rate || ""));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRate = async () => {
    try { await api.put("/settings", { mileage_rate: Number(rate || 0) }); toast.success("Barème enregistré"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async () => {
    if (!f.distance_km) return toast.error("Distance requise");
    try { await api.post("/mileage/trips", { ...f, distance_km: Number(f.distance_km || 0) }); setOpen(false); setF(empty); toast.success("Trajet enregistré"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (r) => { if (!window.confirm("Supprimer ?")) return; await api.delete(`/mileage/trips/${r.id}`); load(); };

  return (
    <div>
      <PageHeader title="Frais de déplacement" subtitle="Suivez vos trajets et calculez vos indemnités kilométriques" icon={Route}
        actions={<Button data-testid="trip-new" onClick={()=>setOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold"><Plus className="w-4 h-4 mr-1" />Nouveau trajet</Button>} />

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6 flex flex-col sm:flex-row sm:items-end gap-3" data-testid="mileage-rate">
        <div className="space-y-1.5 flex-1"><Label className="text-xs text-slate-500">Barème (€ / km)</Label><Input data-testid="rate-input" type="number" step="0.001" value={rate} onChange={(e)=>setRate(e.target.value)} className="bg-slate-50 border-slate-200 max-w-[180px]" /></div>
        <Button data-testid="rate-save" onClick={saveRate} variant="outline" className="border-slate-200">Enregistrer le barème</Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="mileage-summary">
          <Stat label="Trajets (année)" value={summary.trips_year} />
          <Stat label="Km (année)" value={summary.km_year} />
          <Stat label="Indemnités (année)" value={eur(summary.amount_year)} tone="emerald" />
          <Stat label="Indemnités (total)" value={eur(summary.amount_total)} />
        </div>
      )}

      {loading ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div> :
        rows.length === 0 ? <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500" data-testid="trips-empty">Aucun trajet enregistré.</div> : (
        <div className="space-y-2" data-testid="trips-list">
          {rows.map((r) => (
            <div key={r.id} data-testid={`trip-${r.id}`} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{r.label || "Trajet"}{r.round_trip ? " · aller-retour" : ""}</div>
                <div className="text-xs text-slate-500">{r.date} · {r.distance_total} km × {r.rate} €/km</div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <div className="font-mono font-semibold text-slate-900">{eur(r.amount)}</div>
                <Button size="icon" variant="ghost" onClick={()=>del(r)} className="text-red-500 h-7 w-7"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}><DialogContent className="bg-white border-slate-200"><DialogHeader><DialogTitle className="text-slate-900">Nouveau trajet</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Date</Label><Input type="date" data-testid="trip-date" value={f.date} onChange={(e)=>setF({...f,date:e.target.value})} className="bg-slate-50 border-slate-200" /></div>
            <div className="space-y-1.5"><Label className="text-xs text-slate-500">Distance (km)</Label><Input data-testid="trip-km" type="number" value={f.distance_km} onChange={(e)=>setF({...f,distance_km:e.target.value})} className="bg-slate-50 border-slate-200" /></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs text-slate-500">Libellé</Label><Input data-testid="trip-label" value={f.label} onChange={(e)=>setF({...f,label:e.target.value})} className="bg-slate-50 border-slate-200" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="trip-roundtrip" checked={f.round_trip} onChange={(e)=>setF({...f,round_trip:e.target.checked})} />Aller-retour (×2)</label>
        </div>
        <DialogFooter><Button variant="ghost" onClick={()=>setOpen(false)}>Annuler</Button><Button data-testid="trip-save" onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Enregistrer</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}

function Stat({ label, value, tone }) {
  const c = tone === "emerald" ? "text-emerald-600" : "text-slate-900";
  return <div className="bg-white border border-slate-200 rounded-xl p-4"><div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div><div className={`font-heading font-bold mt-1 text-xl ${c}`}>{value}</div></div>;
}
